import type { DateValue } from "@internationalized/date";

import type { ReactNode } from "react";

import { CalendarDate, getLocalTimeZone, isToday, parseDate } from "@internationalized/date";
import clsx from "clsx";
import {
	DateInput as AriaDateInput,
	Button,
	Calendar,
	CalendarCell,
	CalendarGrid,
	CalendarGridBody,
	CalendarGridHeader,
	CalendarHeaderCell,
	DatePicker,
	DateSegment,
	Dialog,
	Group,
	Heading,
	Popover,
} from "react-aria-components";
import { useController, useFormContext } from "react-hook-form";

import { ArrowNextIcon } from "../../../icons/arrow-next";
import { ArrowPrevIcon } from "../../../icons/arrow-prev";
import { CalendarIcon } from "../../../icons/calendar";
import { LabelInputAndError } from "../base-input";

import styles from "./date-input.module.css";

type DateInputProps = {
	className?: string;
	description?: string;
	disabledFromDate?: Date;
	enforceMaxDate?: boolean;
	isDisabled?: boolean;
	label: string;
	name: string;
};

const toCalendarDate = (value: string | undefined): DateValue | null => {
	if (!value) {
		return null;
	}
	try {
		return parseDate(value);
	} catch {
		return null;
	}
};

const jsDateToCalendarDate = (date: Date): CalendarDate => {
	return new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
};

export function DateInput({
	className,
	description,
	disabledFromDate = new Date(),
	enforceMaxDate = true,
	isDisabled,
	label,
	name,
}: DateInputProps): ReactNode {
	const { control } = useFormContext();
	const { field, fieldState } = useController({ control, disabled: isDisabled, name });

	const value = toCalendarDate(field.value as string | undefined);
	const maxValue = enforceMaxDate ? jsDateToCalendarDate(disabledFromDate) : undefined;

	return (
		<DatePicker
			className={clsx(className, styles.wrapper, fieldState.invalid && styles.invalidField)}
			isDisabled={field.disabled}
			isInvalid={fieldState.invalid}
			maxValue={maxValue}
			onBlur={field.onBlur}
			onChange={(next) => {
				return field.onChange(next ? next.toString() : undefined);
			}}
			validationBehavior="aria"
			value={value}
		>
			<LabelInputAndError
				description={description}
				errorMessage={fieldState.error?.message}
				isInvalid={fieldState.invalid}
				label={label}
			>
				<Group className={styles.group}>
					<AriaDateInput className={clsx(styles.input, "body-2")}>
						{(segment) => {
							return <DateSegment className={styles.segment} segment={segment} />;
						}}
					</AriaDateInput>
					<Button aria-label="Calendar" className={styles.triggerButton}>
						<CalendarIcon />
					</Button>
				</Group>
			</LabelInputAndError>
			<Popover className={styles.popover} placement="bottom start">
				<Dialog className={styles.dialog}>
					<Calendar className={styles.calendar}>
						<header className={styles.calendarHeader}>
							<Button className={styles.calendarNavButton} slot="previous">
								<ArrowPrevIcon />
							</Button>
							<Heading className={clsx("body-2", "bold", styles.calendarHeading)} />
							<Button className={styles.calendarNavButton} slot="next">
								<ArrowNextIcon />
							</Button>
						</header>
						<CalendarGrid className={styles.calendarGrid}>
							<CalendarGridHeader>
								{(day) => {
									return (
										<CalendarHeaderCell
											className={clsx("caption", styles.calendarHeaderCell)}
										>
											{day}
										</CalendarHeaderCell>
									);
								}}
							</CalendarGridHeader>
							<CalendarGridBody>
								{(date) => {
									return (
										<CalendarCell
											className={clsx(
												"body-2",
												styles.calendarCell,
												isToday(date, getLocalTimeZone()) &&
													styles.calendarCellToday,
											)}
											date={date}
										/>
									);
								}}
							</CalendarGridBody>
						</CalendarGrid>
					</Calendar>
				</Dialog>
			</Popover>
		</DatePicker>
	);
}
